import { useState, type FormEvent } from 'react';
import { useMsal } from '@azure/msal-react';
import { getLoginRequest } from '../auth/msal-config.js';
import { ApiError } from '../api/client.js';
import { shownPhone, type FamilyDraft } from '../api/coach-clients.js';
import { FamilyForm } from './family-form.js';
import { missingField } from './family-missing-field.js';
import { familyProblem } from './family-problems.js';
import { BusyLabel } from './busy-label.js';

/**
 * "Complete your profile and Hit Different!" (Matthew's words, 31 October; was
 * "Complete your coaching profile", mockup *Clients and Texted Links* v8,
 * screen 6, approved 21 October): what a family's sign-up link opens before
 * they have an account - texted by the coach or emailed, one page for both
 * (ruled 21 October).
 *
 * Everything the coach prepared, in full and editable (Matthew's ruling, made
 * knowing that whoever holds the link sees and can change it): the parent,
 * then each athlete with their own bookings under them, and everyone's birthday
 * asked for here when there is none (31 October). The parent's completes their
 * profile, so no second form follows. A clinic says so, with its title,
 * and no cage. The email is required. Sign up
 * saves it all once, then opens Microsoft's sign-up with that email filled in
 * (prompt=create), and only that email can claim the family when they come
 * back to the same link (welcome-route.tsx).
 *
 * SIGN UP IS ALWAYS TAPPABLE (ruled 24 October, the rule for the whole app):
 * with a box still needed - most often the email, on a family the coach made
 * without one - the tap puts the cursor there and says "This is required."
 * under it. A greyed button that said nothing left a parent unable to go on.
 */

export interface InvitationDetails {
  email: string | null;
  parent_first_name: string;
  parent_last_name: string;
  parent_phone: string | null;
  parent_needs_birthday: boolean;
  athletes: { id: string; first_name: string; last_name: string; needs_birthday: boolean }[];
  lessons: {
    start: string;
    end: string;
    timezone: string;
    facility_name: string;
    cage: string;
    athlete_ids: string[];
    clinic_title: string | null;
  }[];
}

type InvitedLesson = InvitationDetails['lessons'][number];

function Booking({ lesson }: { lesson: InvitedLesson }) {
  return (
    <div className="family-lesson">
      <span className="section-label">{lesson.clinic_title ? 'Clinic' : 'Lesson'}</span>
      {lesson.clinic_title ? (
        <div className="fact">
          <span className="label">Title</span>
          <span className="value">{lesson.clinic_title}</span>
        </div>
      ) : null}
      <div className="fact">
        <span className="label">When</span>
        <span className="value">{when(lesson.start, lesson.end, lesson.timezone)}</span>
      </div>
      <div className="fact">
        <span className="label">Where</span>
        <span className="value">
          {lesson.clinic_title ? lesson.facility_name : `${lesson.facility_name} · ${lesson.cage}`}
        </span>
      </div>
    </div>
  );
}

export function CompleteCoachingProfile({ hash, details }: { hash: string; details: InvitationDetails }) {
  const { instance } = useMsal();
  const [draft, setDraft] = useState<FamilyDraft>({
    parent_first_name: details.parent_first_name,
    parent_last_name: details.parent_last_name,
    parent_phone: shownPhone(details.parent_phone),
    parent_email: details.email ?? '',
    parent_needs_birthday: details.parent_needs_birthday,
    parent_date_of_birth: '',
    athletes: details.athletes.map((athlete) => ({ ...athlete })),
  });
  const unplaced = details.lessons.filter((lesson) => lesson.athlete_ids.length === 0);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<{ field: string; message: string } | null>(null);

  async function signUp(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    const missing = missingField(draft, true);
    if (missing) {
      setProblem({ field: missing.field, message: missing.message });
      // A phone opens its picker on a focused date box and covers whose date it
      // is (Matthew, 31 October), so that box is only brought into view, outlined
      // by data-invalid, and the parent taps it when ready.
      const box = document.getElementById(`welcome-${missing.input}`);
      if (box instanceof HTMLInputElement && box.type === 'date') box.scrollIntoView({ block: 'center' });
      else box?.focus();
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      const response = await fetch(`/api/family-invitations/${hash}`, {
        method: 'PUT',
        headers: {
          'content-type': 'application/json',
          'x-portal-client-id': String(import.meta.env['VITE_ENTRA_CLIENT_ID'] ?? ''),
        },
        body: JSON.stringify(draft),
      });
      const body = (await response.json().catch(() => ({}))) as { email?: string; error?: string; field?: string };
      if (!response.ok || !body.email) {
        throw new ApiError(response.status, body.error ?? 'http_error', 'Not saved', body.field);
      }
      await instance.loginRedirect({ ...getLoginRequest(), loginHint: body.email, prompt: 'create' });
    } catch (cause) {
      setProblem(familyProblem(cause));
      setSaving(false);
    }
  }

  return (
    <form className="panel" onSubmit={(event) => void signUp(event)} noValidate>
      <h2>Complete your profile and Hit Different!</h2>
      <FamilyForm
        value={draft}
        onChange={(next) => {
          setDraft(next);
          setProblem(null);
        }}
        order="parent-first"
        canAddAthletes={false}
        problem={problem}
        idPrefix="welcome"
        autofill
        afterAthlete={(athleteId) =>
          details.lessons
            .filter((lesson) => athleteId !== undefined && lesson.athlete_ids.includes(athleteId))
            .map((lesson) => <Booking key={lesson.start} lesson={lesson} />)
        }
      />
      {unplaced.map((lesson) => (
        <Booking key={lesson.start} lesson={lesson} />
      ))}
      {problem && !problem.field ? <p className="form-note">{problem.message}</p> : null}
      <button type="submit" className="primary" disabled={saving} aria-busy={saving}>
        <BusyLabel busy={saving} working="Signing up…">
          Sign up
        </BusyLabel>
      </button>
    </form>
  );
}

/** "Wed 22 Oct, 17:00 – 18:00", in the facility's own time. */
function when(startIso: string, endIso: string, timeZone: string): string {
  const day = new Date(startIso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone });
  const clock = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone });
  return `${day}, ${clock(startIso)} – ${clock(endIso)}`;
}
